<script setup>
import { ref, computed, onMounted } from 'vue';
import { Link, usePage, router } from '@inertiajs/vue3';
import { route } from 'ziggy-js';

// Get Inertia page
const page = usePage();
const user = computed(() => page.props.user);
const setting = computed(() => page.props.settings || {});

// Sidebar State
const sidebarOpen = ref(true);

// Function to set active menu using jQuery (like absensi project)
const setActiveMenu = () => {
    if (typeof $ !== 'undefined') {
        const currentRoute = window.location.pathname;
        
        // Remove all active classes first
        $('.sidebar-menu li').removeClass('active');
        $('.sidebar-menu .treeview-menu li').removeClass('active');
        
        let found = false;
        
        // Special case for dashboard
        if (currentRoute === '/admin/dashboard' || currentRoute === '/admin' || currentRoute === '/dashboard') {
            const dashboardLink = $('.sidebar-menu a[href*="/dashboard"]');
            if (dashboardLink.length > 0) {
                dashboardLink.parent('li').addClass('active');
                found = true;
            }
        }
        
        // Find and activate current menu item
        if (!found) {
            $('.sidebar-menu a').each(function() {
                const href = $(this).attr('href');
                
                if (href && href !== '#') {
                    let normalizedHref = href;
                    try {
                        const url = new URL(href, window.location.origin);
                        normalizedHref = url.pathname;
                    } catch (e) {
                        normalizedHref = href.startsWith('/') ? href : '/' + href;
                    }
                    
                    // Check exact match first
                    if (normalizedHref === currentRoute) {
                        $(this).parent('li').addClass('active');
                        found = true;
                        
                        // If it's a submenu item, also activate parent
                        const parentTreeview = $(this).closest('.treeview-menu').parent('.treeview');
                        if (parentTreeview.length) {
                            parentTreeview.addClass('menu-open active');
                            parentTreeview.find('> .treeview-menu').show();
                        }
                    }
                    // Check if current route starts with href (for sub-routes like /create, /edit)
                    else if (currentRoute.startsWith(normalizedHref) && normalizedHref !== '/admin' && normalizedHref !== '/') {
                        $(this).parent('li').addClass('active');
                        found = true;
                        
                        const parentTreeview = $(this).closest('.treeview-menu').parent('.treeview');
                        if (parentTreeview.length) {
                            parentTreeview.addClass('menu-open active');
                            parentTreeview.find('> .treeview-menu').show();
                        }
                    }
                }
            });
        }
        
        // Ensure treeview menu stays open for active items
        $('.sidebar-menu li.treeview.menu-open > .treeview-menu').show();
    }
};

// Initialize treeview menu click handlers (like absensi project)
const initTreeviewMenu = () => {
    if (typeof $ !== 'undefined') {
        // Remove existing event handlers to prevent duplicates
        $('.sidebar-menu li.treeview > a').off('click.treeview');
        
        // Initialize sidebar menu manually
        $('.sidebar-menu li.treeview > a').on('click.treeview', function(e) {
            e.preventDefault();
            e.stopPropagation();
            
            const parent = $(this).parent();
            const menu = parent.find('> ul.treeview-menu');
            
            if (parent.hasClass('menu-open')) {
                parent.removeClass('menu-open');
                menu.slideUp(300);
            } else {
                // Close other open menus (accordion behavior)
                $('.sidebar-menu li.treeview').removeClass('menu-open');
                $('.sidebar-menu ul.treeview-menu').slideUp(300);
                
                // Open current menu
                parent.addClass('menu-open');
                menu.slideDown(300);
            }
        });
    }
};

// Close all submenus
const closeAllMenus = () => {
    if (typeof $ !== 'undefined') {
        $('.sidebar-menu li.treeview').removeClass('menu-open');
        $('.sidebar-menu ul.treeview-menu').hide();
    }
};

// Initialize on mount
onMounted(() => {
    // Set active menu and init treeview on initial load
    setTimeout(() => {
        setActiveMenu();
        initTreeviewMenu();
    }, 100);
    setTimeout(() => {
        setActiveMenu();
        initTreeviewMenu();
    }, 300);
    setTimeout(() => {
        setActiveMenu();
        initTreeviewMenu();
    }, 500);
});

// Listen for Inertia navigation events
router.on('navigate', () => {
    setTimeout(() => {
        // Close all menus first, then setActiveMenu will open the correct one
        closeAllMenus();
        setActiveMenu();
        initTreeviewMenu();
    }, 100);
    setTimeout(() => {
        setActiveMenu();
    }, 200);
});

const logout = () => {
    router.post(route('logout'));
};
</script>

<template>
    <div class="wrapper">
        <!-- HEADER (from navbardash.blade.php) -->
        <header class="main-header">
            <div class="d-flex align-items-center logo-box justify-content-start">
                <Link :href="route('dashboard.index')" class="logo">
                    <div class="logo-mini w-50">
                        <span class="light-logo">
                            <img :src="setting.setting_logo_small ? '/storage/logo/' + setting.setting_logo_small : '/storage/logo/logo.webp'" alt="Logo Small">
                        </span>
                        <span class="dark-logo">
                            <img :src="setting.setting_logo_small ? '/storage/logo/' + setting.setting_logo_small : '/storage/logo/logo.webp'" alt="Logo Small">
                        </span>
                    </div>
                    <div class="logo-lg w-150">
                        <span class="light-logo">
                            <img :src="setting.setting_logo_large ? '/storage/logo/' + setting.setting_logo_large : '/storage/logo/logo.webp'" alt="Logo Large">
                        </span>
                        <span class="dark-logo">
                            <img :src="setting.setting_logo_large ? '/storage/logo/' + setting.setting_logo_large : '/storage/logo/logo.webp'" alt="Logo Large">
                        </span>
                    </div>
                </Link>
            </div>
            
            <nav class="navbar navbar-static-top">
                <div class="app-menu">
                    <ul class="header-megamenu nav">
                        <li class="btn-group nav-item">
                            <a href="#" class="waves-effect waves-light nav-link push-btn btn-primary-light ms-0" data-toggle="push-menu" role="button">
                                <i data-feather="menu"></i>
                            </a>
                        </li>
                    </ul>
                </div>

                <div class="navbar-custom-menu r-side">
                    <ul class="nav navbar-nav">
                        <li class="btn-group d-md-inline-flex d-none">
                            <a href="javascript:void(0)" title="skin Change" class="waves-effect skin-toggle waves-light">
                                <label class="switch">
                                    <input type="checkbox" data-mainsidebarskin="toggle" id="toggle_left_sidebar_skin">
                                    <span class="switch-on"><i data-feather="moon"></i></span>
                                    <span class="switch-off"><i data-feather="sun"></i></span>
                                </label>
                            </a>
                        </li>

                        <li class="btn-group nav-item d-xl-inline-flex d-none">
                            <a href="#" data-provide="fullscreen" class="waves-effect waves-light nav-link btn-primary-light svg-bt-icon" title="Full Screen">
                                <i data-feather="maximize"></i>
                            </a>
                        </li>

                        <!-- User Profile (matching Blade style) -->
                        <li class="dropdown user user-menu">
                            <a href="#" class="waves-effect waves-light dropdown-toggle w-auto l-h-12 bg-transparent p-0 no-shadow" title="User" data-bs-toggle="dropdown">
                                <div class="d-flex pt-1 align-items-center">
                                    <div class="text-end me-10 d-none d-lg-block">
                                        <p class="pt-5 fs-14 mb-0 fw-700">{{ user?.name }}</p>
                                        <small class="fs-10 mb-0 text-mute">{{ user?.email }}</small>
                                    </div>
                                    <img src="/back/images/avatar/avatar-13.png" class="avatar rounded-circle bg-primary-light h-40 w-40" alt="User Avatar" />
                                </div>
                            </a>
                            <ul class="dropdown-menu animated flipInX" style="min-width: 280px;">
                                <li class="user-body p-15">
                                    <!-- User Info Header -->
                                    <div class="d-flex flex-row mb-15">
                                        <div><img src="/back/images/avatar/avatar-13.png" alt="user" class="rounded bg-danger-light w-60"></div>
                                        <div class="ps-15">
                                            <h6 class="mb-0">{{ user?.name }}</h6>
                                            <p class="my-5 text-fade fs-12">Administrator</p>
                                            <small class="text-muted">{{ user?.email }}</small>
                                        </div>
                                    </div>
                                    <div class="dropdown-divider"></div>
                                    <!-- Menu Items -->
                                    <Link class="dropdown-item py-10" :href="route('profile.index')">
                                        <i class="ti-user text-muted me-2"></i> Profil Admin
                                    </Link>
                                    <Link class="dropdown-item py-10" :href="route('admin.users.index')">
                                        <i class="ti-settings text-muted me-2"></i> Kelola User
                                    </Link>
                                    <div class="dropdown-divider"></div>
                                    <Link class="dropdown-item py-10 text-danger" :href="route('logout')" method="post" as="button">
                                        <i class="ti-power-off me-2"></i> Keluar
                                    </Link>
                                </li>
                            </ul>
                        </li>
                    </ul>
                </div>
            </nav>
        </header>

        <!-- SIDEBAR (from sidebardash.blade.php) -->
        <aside class="main-sidebar">
            <section class="sidebar position-relative" id="main-sidebar-section">
                <div class="multinav">
                    <div class="multinav-scroll" style="height: 97%;">
                        <ul class="sidebar-menu" data-widget="tree">
                            <li>
                                <Link :href="route('dashboard.index')"><i data-feather="home"></i><span>Dashboard</span></Link>
                            </li>
                            
                            <!-- Konten Website -->
                            <li class="treeview">
                                <a href="#">
                                    <i data-feather="layers"></i>
                                    <span>Konten Website</span>
                                    <span class="pull-right-container"><i class="fa fa-angle-right pull-right"></i></span>
                                </a>
                                <ul class="treeview-menu">
                                    <li><Link :href="route('admin.carousel.index')"><i class="icon-Commit"><span class="path1"></span><span class="path2"></span></i>Carousel</Link></li>
                                    <li><Link :href="route('admin.feature.index')"><i class="icon-Commit"><span class="path1"></span><span class="path2"></span></i>Feature</Link></li>
                                    <li><Link :href="route('admin.faq.index')"><i class="icon-Commit"><span class="path1"></span><span class="path2"></span></i>FAQ</Link></li>
                                    <li><Link :href="route('admin.caption.index')"><i class="icon-Commit"><span class="path1"></span><span class="path2"></span></i>Caption</Link></li>
                                    <li><Link :href="route('admin.profil.index')"><i class="icon-Commit"><span class="path1"></span><span class="path2"></span></i>Profil Perusahaan</Link></li>
                                    <li><Link :href="route('back.syarat-ketentuan.index')"><i class="icon-Commit"><span class="path1"></span><span class="path2"></span></i>Syarat & Ketentuan</Link></li>
                                    <li><Link :href="route('admin.gallery.index')"><i class="icon-Commit"><span class="path1"></span><span class="path2"></span></i>Gallery Foto</Link></li>
                                    <li><Link :href="route('admin.ulasan.index')"><i class="icon-Commit"><span class="path1"></span><span class="path2"></span></i>Ulasan Pelanggan</Link></li>
                                </ul>
                            </li>

                            <!-- Manajemen Mobil -->
                            <li class="treeview">
                                <a href="#">
                                    <i data-feather="shuffle"></i>
                                    <span>Manajemen Mobil</span>
                                    <span class="pull-right-container"><i class="fa fa-angle-right pull-right"></i></span>
                                </a>
                                <ul class="treeview-menu">
                                    <li><Link :href="route('admin.jenis-mobil.index')"><i class="icon-Commit"><span class="path1"></span><span class="path2"></span></i>Jenis Mobil</Link></li>
                                    <li><Link :href="route('admin.mobil.index')"><i class="icon-Commit"><span class="path1"></span><span class="path2"></span></i>Data Mobil</Link></li>
                                    <li><a href="/admin/feed/google-merchant"><i class="icon-Commit"><span class="path1"></span><span class="path2"></span></i>Google Merchant Feed</a></li>
                                </ul>
                            </li>

                            <!-- Manajemen Artikel -->
                            <li class="treeview">
                                <a href="#">
                                    <i data-feather="edit-3"></i>
                                    <span>Manajemen Artikel</span>
                                    <span class="pull-right-container"><i class="fa fa-angle-right pull-right"></i></span>
                                </a>
                                <ul class="treeview-menu">
                                    <li><Link :href="route('admin.category.index')"><i class="icon-Commit"><span class="path1"></span><span class="path2"></span></i>Kategori Artikel</Link></li>
                                    <li><Link :href="route('admin.news.index')"><i class="icon-Commit"><span class="path1"></span><span class="path2"></span></i>Berita & Artikel</Link></li>
                                </ul>
                            </li>

                            <!-- SEO & Marketing -->
                            <li class="treeview">
                                <a href="#">
                                    <i data-feather="map-pin"></i>
                                    <span>SEO & Marketing</span>
                                    <span class="pull-right-container"><i class="fa fa-angle-right pull-right"></i></span>
                                </a>
                                <ul class="treeview-menu">
                                    <li><Link :href="route('back.kecamatan.index')"><i class="icon-Commit"><span class="path1"></span><span class="path2"></span></i>Manajemen Kecamatan</Link></li>
                                    <li><Link :href="route('back.artikel-kecamatan.index')"><i class="icon-Commit"><span class="path1"></span><span class="path2"></span></i>Artikel Kecamatan</Link></li>
                                    <li><Link :href="route('admin.open-trip.index')"><i class="icon-Commit"><span class="path1"></span><span class="path2"></span></i>Open Trip</Link></li>
                                </ul>
                            </li>

                            <!-- Security & Logs -->
                            <li class="treeview">
                                <a href="#">
                                    <i data-feather="shield"></i>
                                    <span>Security & Logs</span>
                                    <span class="pull-right-container"><i class="fa fa-angle-right pull-right"></i></span>
                                </a>
                                <ul class="treeview-menu">
                                    <li><Link :href="route('admin.login-history.index')"><i class="icon-Commit"><span class="path1"></span><span class="path2"></span></i>Login History</Link></li>
                                    <li><Link :href="route('admin.failed-login.index')"><i class="icon-Commit"><span class="path1"></span><span class="path2"></span></i>Failed Attempts</Link></li>
                                </ul>
                            </li>

                            <!-- Setting -->
                            <li class="treeview">
                                <a href="#">
                                    <i data-feather="settings"></i>
                                    <span>Setting</span>
                                    <span class="pull-right-container"><i class="fa fa-angle-right pull-right"></i></span>
                                </a>
                                <ul class="treeview-menu">
                                    <li><Link :href="route('admin.setting.index')"><i class="icon-Commit"><span class="path1"></span><span class="path2"></span></i>Setting Website</Link></li>
                                    <li><Link :href="route('admin.auto-link-keywords.index')"><i class="icon-Commit"><span class="path1"></span><span class="path2"></span></i>Auto Link Keywords</Link></li>
                                    <li><Link :href="route('admin.seo-meta-checker.index')"><i class="icon-Commit"><span class="path1"></span><span class="path2"></span></i>SEO Meta Checker</Link></li>
                                </ul>
                            </li>
                            
                            <!-- Documentation -->
                            <li class="treeview">
                                <a href="#">
                                    <i data-feather="book-open"></i>
                                    <span>Dokumentasi</span>
                                    <span class="pull-right-container"><i class="fa fa-angle-right pull-right"></i></span>
                                </a>
                                <ul class="treeview-menu">
                                    <li><Link :href="route('admin.documentation.index')"><i class="icon-Commit"><span class="path1"></span><span class="path2"></span></i>Overview</Link></li>
                                </ul>
                            </li>
                        </ul>
                        
                        <div class="sidebar-widgets">
                            <div class="mx-25 mb-30 pb-20 side-bx bg-primary-light rounded20">
                                <div class="text-center">
                                    <img src="/back/images/svg-icon/color-svg/custom-24.svg" class="sideimg p-5" alt="">
                                    <h4 class="title-bx text-primary">{{ setting.setting_title || 'Gotransport' }} Team</h4>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </aside>

        <!-- MAIN CONTENT SLOT -->
        <div class="content-wrapper">
             <slot />
        </div>

        <!-- FOOTER -->
        <footer class="main-footer">
            &copy; {{ new Date().getFullYear() }} <a :href="setting.setting_url || '#'">{{ setting.setting_title || 'Title Data' }}</a>. All Rights Reserved.
        </footer>
    </div>
</template>
